import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab
} from '@mui/material'
import {
  Settings,
  Save,
  Refresh,
  Add,
  Edit,
  Delete,
  People,
  Group,
  MenuBook,
  Gavel,
  LibraryBooks,
  Tune,
  SmartToy
} from '@mui/icons-material'
import axios from 'axios'
import { useAuth } from '../../contexts/AuthContext'

// Importar componentes de las pestañas
import UsuariosAdmin from '../usuarios/UsuariosAdmin'
import RolesResponsabilidad from './RolesResponsabilidad'
import DefinicionesAdmin from './DefinicionesAdmin'
import NormatividadAdmin from './NormatividadAdmin'
import BaseConocimiento from './BaseConocimiento'
import ContextosIA from './ContextosIA'

// Componente de Panel para las pestañas
function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`config-tabpanel-${index}`}
      aria-labelledby={`config-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  )
}

const ConfiguracionSistema = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [configuraciones, setConfiguraciones] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [openDialog, setOpenDialog] = useState(false)
  const [selectedConfig, setSelectedConfig] = useState(null)
  const [activeTab, setActiveTab] = useState(0)
  const [formData, setFormData] = useState({
    clave: '',
    valor: '',
    tipo: 'string',
    descripcion: '',
    es_publica: false
  })

  const tabs = [
    { label: 'Usuarios', icon: <People />, component: 'usuarios', path: '/configuracion/usuarios' },
    { label: 'Responsables', icon: <Group />, component: 'responsables', path: '/configuracion/responsables' },
    { label: 'Definiciones', icon: <MenuBook />, component: 'definiciones', path: '/configuracion/definiciones' },
    { label: 'Normatividad', icon: <Gavel />, component: 'normatividad', path: '/configuracion/normatividad' },
    { label: 'Base de Conocimiento', icon: <LibraryBooks />, component: 'conocimiento', path: '/configuracion/conocimiento' },
    { label: 'Contextos de IA', icon: <SmartToy />, component: 'contextos-ia', path: '/configuracion/contextos-ia' },
    { label: 'Variables del Sistema', icon: <Tune />, component: 'variables', path: '/configuracion/sistema' }
  ]

  useEffect(() => {
    loadConfiguraciones()
  }, [])

  useEffect(() => {
    // Determinar qué pestaña mostrar basándose en la URL
    const currentPath = location.pathname
    const tabIndex = tabs.findIndex(tab => tab.path === currentPath)
    if (tabIndex !== -1) {
      setActiveTab(tabIndex)
    }
  }, [location.pathname])

  const loadConfiguraciones = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get('/api/config', {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      // Asegurar que response.data es un array
      if (Array.isArray(response.data)) {
        setConfiguraciones(response.data)
      } else {
        console.error('La respuesta no es un array:', response.data)
        setConfiguraciones([])
        setError('Formato de datos incorrecto del servidor')
      }
    } catch (error) {
      console.error('Error al cargar configuraciones:', error)
      setConfiguraciones([])
      setError('Error al cargar configuraciones: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setSelectedConfig(null)
    setFormData({
      clave: '',
      valor: '',
      tipo: 'string',
      descripcion: '',
      es_publica: false
    })
    setOpenDialog(true)
  }

  const handleEdit = (config) => {
    setSelectedConfig(config)
    setFormData({
      clave: config.clave,
      valor: config.valor,
      tipo: config.tipo,
      descripcion: config.descripcion,
      es_publica: config.es_publica
    })
    setOpenDialog(true)
  }

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token')
      
      if (selectedConfig) {
        // Editar
        await axios.put(`/api/config/${selectedConfig.clave}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        })
        setSuccess('Configuración actualizada exitosamente')
      } else {
        // Crear
        await axios.post('/api/config', formData, {
          headers: { Authorization: `Bearer ${token}` }
        })
        setSuccess('Configuración creada exitosamente')
      }
      
      setOpenDialog(false)
      loadConfiguraciones()
    } catch (error) {
      setError('Error al guardar: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleDelete = async (config) => {
    if (!window.confirm(`¿Está seguro de eliminar la configuración "${config.clave}"?`)) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      await axios.delete(`/api/config/${config.clave}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSuccess('Configuración eliminada exitosamente')
      loadConfiguraciones()
    } catch (error) {
      setError('Error al eliminar: ' + (error.response?.data?.detail || error.message))
    }
  }

  // Función para verificar si el usuario puede ver/editar una configuración
  const canAccessConfig = (config) => {
    // Solo administradores pueden ver configuraciones de IA
    if (config.categoria === 'ia' || config.clave === 'ai.deepseek_api_key') {
      return user?.rol === 'administrador'
    }
    return true // Otras configuraciones son visibles para todos
  }

  const canEditConfig = (config) => {
    // Solo administradores pueden editar cualquier configuración
    return user?.rol === 'administrador'
  }

  const renderValue = (config) => {
    // Ocultar parcialmente las API keys por seguridad
    if (config.clave === 'ai.deepseek_api_key' && config.valor && config.valor.length > 10) {
      const start = config.valor.substring(0, 6)
      const end = config.valor.substring(config.valor.length - 4)
      return `${start}...${end} 🔑`
    }

    switch (config.tipo) {
      case 'boolean':
        return config.valor === 'true' ? 'Sí' : 'No'
      case 'json':
        try {
          return JSON.stringify(JSON.parse(config.valor), null, 2)
        } catch {
          return config.valor
        }
      default:
        return config.valor || 'Sin valor'
    }
  }

  const renderFormField = () => {
    switch (formData.tipo) {
      case 'boolean':
        return (
          <FormControlLabel
            control={
              <Switch
                checked={formData.valor === 'true'}
                onChange={(e) => setFormData({ ...formData, valor: e.target.checked ? 'true' : 'false' })}
              />
            }
            label="Valor"
          />
        )
      case 'number':
        return (
          <TextField
            fullWidth
            label="Valor"
            type="number"
            value={formData.valor}
            onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
            required
          />
        )
      case 'json':
        return (
          <TextField
            fullWidth
            label="Valor JSON"
            multiline
            rows={4}
            value={formData.valor}
            onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
            helperText="Debe ser un JSON válido"
            required
          />
        )
      default:
        return (
          <TextField
            fullWidth
            label="Valor"
            type={formData.clave === 'ai.deepseek_api_key' ? 'password' : 'text'}
            value={formData.valor}
            onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
            placeholder={formData.clave === 'ai.deepseek_api_key' ? 'sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx' : ''}
            helperText={formData.clave === 'ai.deepseek_api_key' ? 'Clave de API de DeepSeek para generación con IA' : ''}
            required
          />
        )
    }
  }

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Cargando configuraciones...</Typography>
      </Box>
    )
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
    // Navegar a la URL correspondiente
    const selectedTab = tabs[newValue]
    if (selectedTab && selectedTab.path) {
      navigate(selectedTab.path)
    }
  }

  const renderTabContent = () => {
    const currentTab = tabs[activeTab]
    
    switch (currentTab.component) {
      case 'variables':
        return renderVariablesTab()
      case 'usuarios':
        return <UsuariosAdmin />
      case 'responsables':
        return <RolesResponsabilidad />
      case 'definiciones':
        return <DefinicionesAdmin />
      case 'normatividad':
        return <NormatividadAdmin />
      case 'conocimiento':
        return <BaseConocimiento />
      case 'contextos-ia':
        return <ContextosIA />
      default:
        return renderVariablesTab()
    }
  }

  const renderVariablesTab = () => (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Variables del Sistema
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadConfiguraciones}
          >
            Recargar
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreate}
          >
            Nueva Variable
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {configuraciones && configuraciones.length > 0 ? configuraciones.filter(canAccessConfig).map((config) => (
          <Grid item xs={12} md={6} lg={4} key={config.clave}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" component="div" gutterBottom>
                    {config.clave}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {config.tipo}
                    </Typography>
                    {config.es_publica && (
                      <Typography variant="caption" color="primary" sx={{ ml: 1 }}>
                        Público
                      </Typography>
                    )}
                    {config.clave === 'ai.deepseek_api_key' && (
                      <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                        🔒 Sensible
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" paragraph>
                  {config.descripcion}
                </Typography>

                <Divider sx={{ my: 1 }} />

                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Valor:
                  </Typography>
                  <Box sx={{ 
                    bgcolor: 'grey.100', 
                    p: 1, 
                    borderRadius: 1, 
                    mt: 0.5,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    maxHeight: 100,
                    overflow: 'auto'
                  }}>
                    {renderValue(config)}
                  </Box>
                </Box>
              </CardContent>

              <CardActions sx={{ justifyContent: 'flex-end' }}>
                {canEditConfig(config) && (
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(config)}
                    title="Editar"
                  >
                    <Edit />
                  </IconButton>
                )}
                {canEditConfig(config) && (
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(config)}
                    title="Eliminar"
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                )}
                {!canEditConfig(config) && (
                  <Typography variant="caption" color="text.secondary">
                    Solo administradores
                  </Typography>
                )}
              </CardActions>
            </Card>
          </Grid>
        )) : null}

        {configuraciones && configuraciones.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No hay variables del sistema configuradas
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Dialog para crear/editar variables */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedConfig ? 'Editar Variable' : 'Nueva Variable'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Clave"
              value={formData.clave}
              onChange={(e) => setFormData({ ...formData, clave: e.target.value })}
              disabled={!!selectedConfig}
              required
            />

            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={formData.tipo}
                label="Tipo"
                onChange={(e) => setFormData({ ...formData, tipo: e.target.value, valor: '' })}
              >
                <MenuItem value="string">Texto</MenuItem>
                <MenuItem value="number">Número</MenuItem>
                <MenuItem value="boolean">Booleano</MenuItem>
                <MenuItem value="json">JSON</MenuItem>
              </Select>
            </FormControl>

            {renderFormField()}

            <TextField
              fullWidth
              label="Descripción"
              multiline
              rows={2}
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.es_publica}
                  onChange={(e) => setFormData({ ...formData, es_publica: e.target.checked })}
                />
              }
              label="Variable pública (accesible sin autenticación)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Settings sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Configuración del Sistema
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 64,
              textTransform: 'none',
              fontSize: '0.875rem'
            }
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              icon={tab.icon}
              iconPosition="start"
              label={tab.label}
              id={`config-tab-${index}`}
              aria-controls={`config-tabpanel-${index}`}
            />
          ))}
        </Tabs>
      </Paper>

      {renderTabContent()}
    </Box>
  )
}

export default ConfiguracionSistema